<template>
  <div class="theme-decorations" aria-hidden="true">
    <div class="theme-decorations__app"></div>
    <div
      v-if="animationEnabled"
      class="theme-decorations__animation"
      :class="[
        `theme-decorations__animation--${animationType}`,
        { 'theme-decorations__animation--login': isLoginPage },
      ]"
    >
      <span
        v-for="particle in particles"
        :key="particle.id"
        class="theme-decorations__particle"
        :style="particle.style"
      ></span>
    </div>
  </div>
</template>

<script>
import statesEnum from "@/enums/statesEnum";

const ALLOWED_TYPES = new Set(["leaf", "snow", "petal", "cloud"]);
const LANE_OFFSETS = [0.3, 0.55, 0.75, 0.42];

export default {
  name: "ThemeDecorations",
  data: () => ({
    pageVisible: document.visibilityState === "visible",
    reducedMotion: false,
    motionQuery: null,
    resizeObserver: null,
    layoutTimer: null,
    particlePositions: [],
  }),
  watch: {
    "$route.fullPath"() {
      this.scheduleDecorationBounds();
    },
  },
  computed: {
    isLoginPage() {
      return [statesEnum.LOGIN, statesEnum.SELECT_LESSON_PAGE].includes(
        this.$route.name
      );
    },
    animation() {
      return this.$store.getters["ThemeStore/getConfig"]?.animation || {};
    },
    animationType() {
      return ALLOWED_TYPES.has(this.animation.type)
        ? this.animation.type
        : "none";
    },
    animationEnabled() {
      const preferences = this.$store.getters["ThemeStore/getPreferences"];
      return Boolean(
        this.animationType !== "none" &&
          preferences.animations_enabled &&
          this.pageVisible &&
          !this.reducedMotion &&
          this.particlePositions.length
      );
    },
    particles() {
      const count = this.animation.density === "medium" ? 8 : 5;
      const duration = Math.min(
        30000,
        Math.max(1000, Number(this.animation.duration_ms) || 12000)
      );
      return Array.from({ length: count }, (_, index) => {
        const position = this.particlePositions[
          index % this.particlePositions.length
        ];
        return {
        id: index,
        style: {
          "--theme-particle-left": `${position.left}px`,
          "--theme-particle-delay": `${-Math.round(
            (duration / count) * index
          )}ms`,
          "--theme-particle-duration": `${duration + (index % 3) * 900}ms`,
          "--theme-particle-drift": `${position.drift}px`,
          "--theme-particle-scale": 0.86 + (index % 3) * 0.14,
        },
      };
      });
    },
  },
  mounted() {
    this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reducedMotion = this.motionQuery.matches;
    document.addEventListener("visibilitychange", this.handleVisibility);
    window.addEventListener("resize", this.scheduleDecorationBounds);
    this.observeDecorationBounds();
    this.layoutTimer = window.setTimeout(this.observeDecorationBounds, 300);
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener("change", this.handleMotionPreference);
    } else {
      this.motionQuery.addListener(this.handleMotionPreference);
    }
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.handleVisibility);
    window.removeEventListener("resize", this.scheduleDecorationBounds);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.layoutTimer) window.clearTimeout(this.layoutTimer);
    if (!this.motionQuery) return;
    if (this.motionQuery.removeEventListener) {
      this.motionQuery.removeEventListener(
        "change",
        this.handleMotionPreference
      );
    } else {
      this.motionQuery.removeListener(this.handleMotionPreference);
    }
  },
  methods: {
    observeDecorationBounds() {
      this.$nextTick(() => {
        const layout = this.findLayoutElements();
        if (!layout) return;
        const { root, content } = layout;
        if (window.ResizeObserver) {
          if (this.resizeObserver) this.resizeObserver.disconnect();
          this.resizeObserver = new ResizeObserver(this.syncDecorationBounds);
          this.resizeObserver.observe(root);
          if (content) this.resizeObserver.observe(content);
        }
        this.syncDecorationBounds();
      });
    },
    findLayoutElements() {
      const main = document.querySelector(".v-main");
      if (main) {
        return { root: main, content: this.findMainContent(main) };
      }
      const login = document.querySelector(".student-login, .demo-login");
      if (login) {
        return {
          root: login,
          content: login.querySelector(
            ".student-login__card, .demo-login__card"
          ),
        };
      }
      return null;
    },
    findMainContent(main) {
      return (
        main.querySelector("[class*='__inner']") ||
        main.querySelector(".v-main__wrap > *") ||
        main.firstElementChild
      );
    },
    scheduleDecorationBounds() {
      if (this.layoutTimer) window.clearTimeout(this.layoutTimer);
      this.layoutTimer = window.setTimeout(this.observeDecorationBounds, 300);
    },
    syncDecorationBounds() {
      const layout = this.findLayoutElements();
      if (!layout || !layout.content) {
        this.particlePositions = [];
        return;
      }
      const { root, content } = layout;
      const mainRect = root.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      const mainStyle = window.getComputedStyle(root);
      const usableLeft = mainRect.left + parseFloat(mainStyle.paddingLeft || 0);
      const usableRight = mainRect.right - parseFloat(mainStyle.paddingRight || 0);
      const lanes = [];
      const addLane = (start, width, drift) => {
        if (width < 28) return;
        LANE_OFFSETS.forEach((offset) => {
          lanes.push({ left: Math.round(start + width * offset), drift });
        });
      };
      addLane(usableLeft, contentRect.left - usableLeft, -16);
      addLane(contentRect.right, usableRight - contentRect.right, 16);
      this.particlePositions = lanes;
    },
    handleVisibility() {
      this.pageVisible = document.visibilityState === "visible";
    },
    handleMotionPreference(event) {
      this.reducedMotion = event.matches;
    },
  },
};
</script>

<style scoped>
.theme-decorations,
.theme-decorations > div {
  inset: 0;
  pointer-events: none;
  position: fixed;
}

.theme-decorations {
  overflow: hidden;
  z-index: 5;
}

.theme-decorations__app {
  background-image: var(--theme-app-decoration-image);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.theme-decorations__animation {
  contain: strict;
  top: 64px;
}

.theme-decorations__animation--login {
  top: 0;
}

.theme-decorations__particle {
  --theme-particle-opacity: 0.34;
  animation: theme-particle-drift var(--theme-particle-duration) linear infinite;
  animation-delay: var(--theme-particle-delay);
  background: var(--theme-accent-decorative);
  filter: drop-shadow(0 2px 3px rgba(31, 41, 55, 0.08));
  height: 13px;
  left: var(--theme-particle-left);
  opacity: 0;
  position: absolute;
  top: -24px;
  transform: scale(var(--theme-particle-scale));
  width: 13px;
}

.theme-decorations__animation--leaf .theme-decorations__particle {
  border-radius: 85% 12% 85% 12%;
}

.theme-decorations__animation--petal .theme-decorations__particle {
  border-radius: 85% 20% 85% 20%;
  background: #d98da1;
  height: 10px;
  width: 15px;
}

.theme-decorations__animation--snow .theme-decorations__particle {
  --theme-particle-opacity: 0.72;
  background: transparent;
  height: 18px;
  width: 18px;
}

.theme-decorations__animation--snow .theme-decorations__particle::before {
  color: #f7fbff;
  content: "❄";
  display: block;
  font-size: 20px;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(54, 95, 133, 0.72);
}

.theme-decorations__animation--cloud .theme-decorations__particle {
  border-radius: 999px;
  height: 9px;
  width: 24px;
}

@keyframes theme-particle-drift {
  0% {
    opacity: 0;
    transform: translate3d(0, -24px, 0) rotate(0deg)
      scale(var(--theme-particle-scale));
  }
  12%,
  82% {
    opacity: var(--theme-particle-opacity);
  }
  100% {
    opacity: 0;
    transform: translate3d(var(--theme-particle-drift), calc(100vh + 32px), 0)
      rotate(240deg) scale(var(--theme-particle-scale));
  }
}

@media (prefers-reduced-motion: reduce) {
  .theme-decorations__animation {
    display: none;
  }
}

@media (max-width: 700px) {
  .theme-decorations__animation {
    display: none;
  }
}
</style>
